import { createContext, useContext } from 'react';

/**
 * NavGuardContext — lets descendants route in-app navigation through a
 * guard supplied by a parent (typically the Viewer's hasUnsavedChanges
 * check that opens UnsavedWarningDialog).
 *
 * The guard takes a `() => void` action and either runs it immediately
 * (no pending state to lose) or stashes it pending user confirmation
 * (Stay / Leave).
 *
 * Default is a passthrough — outside a provider, the action runs as-is
 * so non-Viewer surfaces (Dashboard, marketing pages) keep working with
 * no extra wiring.
 *
 * Usage:
 *   <NavGuardContext.Provider value={{ guardedNavigate }}>
 *     ...children that trigger navigation can call useNavGuard()...
 *   </NavGuardContext.Provider>
 *
 *   // In any descendant:
 *   const guard = useNavGuard();
 *   const onClick = () => guard(() => navigate('/account/billing'));
 */
const NavGuardContext = createContext({
  guardedNavigate: (action) => action(),
});

export function useNavGuard() {
  const ctx = useContext(NavGuardContext);
  return ctx.guardedNavigate;
}

export default NavGuardContext;
